import { ReactNode, ButtonHTMLAttributes } from "react";
import { Loader2 } from "lucide-react";
import { getContrastTextColor } from "../../lib/color-utils";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "outline"
  | "danger"
  | "ghost";
export type ButtonSize = "sm" | "md";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  primaryColor?: string;
  children: ReactNode;
  iconOnly?: boolean;
}

export function Button({
  variant = "primary",
  size = "md",
  isLoading = false,
  leftIcon,
  rightIcon,
  primaryColor = "#0F52BA", // Krude Sapphire
  className = "",
  style = {},
  children,
  disabled,
  iconOnly = false,
  ...props
}: ButtonProps) {
  // Base styles: uppercase tracking, rounding, display
  const baseStyles =
    "inline-flex items-center justify-center font-bold uppercase tracking-widest transition-all duration-200 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed rounded-[10px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2";

  // Size-specific styles (h-11 vs h-8)
  const sizeStyles = {
    sm: "h-8 px-3 text-[10px] gap-1.5",
    md: "h-11 px-5 text-[11px] gap-2.5",
  };

  // Variant-specific styles
  const getVariantStyles = () => {
    switch (variant) {
      case "primary":
        return {
          classes: "text-white shadow-sm focus-visible:ring-blue-500",
          customBg: true,
        };
      case "secondary":
        return {
          classes:
            "bg-slate-100 text-slate-600 hover:bg-slate-200 focus-visible:ring-slate-300",
          customBg: false,
        };
      case "outline":
        return {
          classes:
            "bg-transparent border border-slate-200 text-slate-600 hover:bg-slate-50 focus-visible:ring-slate-200",
          customBg: false,
        };
      case "danger":
        return {
          classes:
            "bg-red-50 text-red-600 border border-red-100 hover:bg-red-100 focus-visible:ring-red-300",
          customBg: false,
        };
      case "ghost":
        return {
          classes:
            "bg-transparent text-slate-500 hover:text-slate-900 hover:bg-slate-100/50 focus-visible:ring-slate-100",
          customBg: false,
        };
      default:
        return { classes: "", customBg: false };
    }
  };

  const variantStyles = getVariantStyles();

  // Final style object
  const finalStyle = variantStyles.customBg
    ? {
        backgroundColor: primaryColor,
        color: getContrastTextColor(primaryColor),
        ...style,
      }
    : style;

  return (
    <button
      className={`${baseStyles} ${sizeStyles[size]} ${variantStyles.classes} ${className} ${iconOnly ? (size === "sm" ? "w-8 px-0" : "w-10 px-0") : ""}`}
      style={finalStyle}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading ? (
        <Loader2 className="animate-spin" size={size === "sm" ? 14 : 16} />
      ) : (
        <>
          {leftIcon && <span className="flex-shrink-0">{leftIcon}</span>}
          {children && (
            <span
              className={`inline-flex items-center justify-center ${iconOnly ? "" : ""}`}
            >
              {children}
            </span>
          )}
          {rightIcon && <span className="flex-shrink-0">{rightIcon}</span>}
        </>
      )}
    </button>
  );
}
